import { useCallback, useMemo, useRef, useState } from 'react';
import { useInvitationGuests } from '@/hooks/useInvitations';
import {
  BulkInsertGuestRow,
  useBulkInsertGuests,
} from '@/hooks/useGuests';
import {
  CSV_TEMPLATE,
  CSVParseResult,
  parseCSVFile,
} from '@/lib/csvGuests';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertCircle, CheckCircle2, Download, FileUp, Loader2, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type Invitation = { id: string; bride_name: string; groom_name: string };

interface CSVImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invitations: Invitation[];
  defaultInvitationId?: string;
  onImported?: () => void;
}

const PREVIEW_LIMIT = 10;

export function CSVImportModal({
  open,
  onOpenChange,
  invitations,
  defaultInvitationId,
  onImported,
}: CSVImportModalProps) {
  const [invitationId, setInvitationId] = useState(
    defaultInvitationId || invitations[0]?.id || '',
  );
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<CSVParseResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkInsert = useBulkInsertGuests();
  const { data: existingGuests = [] } = useInvitationGuests(invitationId);
  const existingPhones = useMemo(
    () => new Set(existingGuests.map((g) => g.guest_phone).filter(Boolean) as string[]),
    [existingGuests],
  );

  const reset = useCallback(() => {
    setFileName(null);
    setParseResult(null);
    setIsParsing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(
    (next: boolean) => {
      if (!next) reset();
      onOpenChange(next);
    },
    [onOpenChange, reset],
  );

  const handleFile = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      toast.error('Solo se aceptan archivos .csv');
      return;
    }
    setFileName(file.name);
    setIsParsing(true);
    try {
      const result = await parseCSVFile(file);
      setParseResult(result);
      if (result.valid.length === 0 && result.invalid.length === 0) {
        toast.error('El archivo está vacío o no tiene datos válidos');
      }
    } catch (err) {
      toast.error('No se pudo leer el archivo CSV');
      console.error(err);
      setParseResult(null);
    } finally {
      setIsParsing(false);
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla-invitados.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const duplicatesAgainstDb = useMemo(() => {
    if (!parseResult) return 0;
    let count = 0;
    const seen = new Set<string>();
    for (const row of parseResult.valid) {
      if (existingPhones.has(row.whatsapp) || seen.has(row.whatsapp)) count += 1;
      seen.add(row.whatsapp);
    }
    return count;
  }, [parseResult, existingPhones]);

  const importable = (parseResult?.valid.length ?? 0) - duplicatesAgainstDb;

  const handleImport = async () => {
    if (!parseResult || parseResult.valid.length === 0 || !invitationId) return;
    const rows: BulkInsertGuestRow[] = parseResult.valid.map((r) => ({
      guest_name: r.nombre,
      guest_phone: r.whatsapp,
      passes_count: r.pases,
      group_type: r.grupo,
      display_label: r.rotulo ?? null,
    }));
    const result = await bulkInsert.mutateAsync({ invitation_id: invitationId, rows });
    if (result.errors === 0) {
      onImported?.();
      handleClose(false);
    }
  };

  const validPreview = parseResult?.valid.slice(0, PREVIEW_LIMIT) ?? [];
  const invalidPreview = parseResult?.invalid.slice(0, PREVIEW_LIMIT) ?? [];
  const validTotal = parseResult?.valid.length ?? 0;
  const invalidTotal = parseResult?.invalid.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar invitados desde CSV</DialogTitle>
          <DialogDescription>
            Sube un archivo CSV con tu lista de invitados. Descarga la plantilla para ver el formato esperado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {invitations.length > 1 && (
            <div className="space-y-1">
              <label className="text-sm font-medium">Invitación destino</label>
              <Select value={invitationId} onValueChange={setInvitationId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar invitación" />
                </SelectTrigger>
                <SelectContent>
                  {invitations.map((inv) => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.bride_name} & {inv.groom_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Descargar plantilla
            </Button>
            {parseResult && (
              <Button variant="ghost" size="sm" onClick={reset}>
                <X className="w-4 h-4 mr-2" />
                Reiniciar
              </Button>
            )}
          </div>

          {!parseResult && !isParsing && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors',
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/30',
              )}
            >
              <FileUp className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">Arrastra un archivo CSV o haz click para seleccionar</p>
              <p className="text-xs text-muted-foreground mt-1">
                Columnas requeridas: nombre, pases, whatsapp, grupo (opcional)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
            </div>
          )}

          {isParsing && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Procesando archivo...</span>
            </div>
          )}

          {parseResult && (
            <div className="space-y-4">
              {fileName && (
                <div className="text-sm text-muted-foreground">
                  Archivo: <span className="font-medium text-foreground">{fileName}</span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border bg-green-50 dark:bg-green-950/30 p-3">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-xs font-medium">Válidos</span>
                  </div>
                  <p className="text-xl font-semibold mt-1">{validTotal}</p>
                </div>
                <div className="rounded-md border bg-amber-50 dark:bg-amber-950/30 p-3">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs font-medium">Duplicados</span>
                  </div>
                  <p className="text-xl font-semibold mt-1">{duplicatesAgainstDb}</p>
                </div>
                <div className="rounded-md border bg-red-50 dark:bg-red-950/30 p-3">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs font-medium">Con errores</span>
                  </div>
                  <p className="text-xl font-semibold mt-1">{invalidTotal}</p>
                </div>
              </div>

              {validTotal > 0 && (
                <div className="rounded-md border overflow-hidden">
                  <div className="bg-green-50 dark:bg-green-950/20 px-3 py-2 text-xs font-medium text-green-700 dark:text-green-400 border-b">
                    Vista previa (primeros {Math.min(PREVIEW_LIMIT, validTotal)} de {validTotal})
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Pases</TableHead>
                        <TableHead>WhatsApp</TableHead>
                        <TableHead>Grupo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {validPreview.map((row, idx) => {
                        const isDup = existingPhones.has(row.whatsapp);
                        return (
                          <TableRow
                            key={idx}
                            className={isDup ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}
                          >
                            <TableCell>{row.nombre}</TableCell>
                            <TableCell>{row.pases}</TableCell>
                            <TableCell className="font-mono text-xs">{row.whatsapp}</TableCell>
                            <TableCell>
                              {row.grupo}
                              {isDup && (
                                <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                                  (ya existe)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {invalidTotal > 0 && (
                <div className="rounded-md border overflow-hidden">
                  <div className="bg-red-50 dark:bg-red-950/20 px-3 py-2 text-xs font-medium text-red-700 dark:text-red-400 border-b">
                    Filas con errores (primeras {Math.min(PREVIEW_LIMIT, invalidTotal)} de {invalidTotal})
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Fila</TableHead>
                        <TableHead>Datos</TableHead>
                        <TableHead>Errores</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invalidPreview.map((row) => (
                        <TableRow key={row.rowNumber} className="bg-red-50/40 dark:bg-red-950/10">
                          <TableCell className="font-mono text-xs">{row.rowNumber}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {row.row.nombre || '—'} · {row.row.whatsapp || '—'}
                          </TableCell>
                          <TableCell>
                            <ul className="text-xs text-red-700 dark:text-red-400 list-disc list-inside space-y-0.5">
                              {row.errors.map((e, i) => (
                                <li key={i}>{e}</li>
                              ))}
                            </ul>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleImport}
            disabled={
              !parseResult ||
              importable <= 0 ||
              !invitationId ||
              bulkInsert.isPending
            }
          >
            {bulkInsert.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Importar {Math.max(0, importable)} invitado{importable === 1 ? '' : 's'} válido{importable === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CSVImportModal;
